/**
 * Iter 41.37 — "Request Suite Activation" from a CRM lead linked to an
 * existing client. Thin wrapper over the existing
 * `/api/admin/suite-activation-requests` endpoint. Visible only when the
 * lead has `linked_company_id`.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

const SUITE_CODES = [
  'talentmap', 'talentcheck', 'talentstart', 'talentheart', 'talentfix',
  'climara', 'talentcompetency', 'talenttrain', 'intelligence',
];
const URGENCIES = ['low', 'medium', 'high', 'urgent'];

export default function RequestSuiteActivationDialog({ open, onOpenChange, lead, onSubmitted }) {
  const { t } = useTranslation();
  const [suiteCode, setSuiteCode] = useState('talentmap');
  const [justification, setJustification] = useState('');
  const [useCase, setUseCase] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [saving, setSaving] = useState(false);

  if (!lead) return null;

  const submit = async () => {
    if (justification.trim().length < 4 || useCase.trim().length < 4) {
      toast.error(t('pages.crm.requestSuite.validationMissing', {
        defaultValue: 'Please fill justification and use case (4+ chars each).',
      }));
      return;
    }
    setSaving(true);
    try {
      const doc = await adminApi.requestSuiteActivationFromLead(lead.id, {
        suite_code: suiteCode,
        justification: justification.trim(),
        use_case: useCase.trim(),
        urgency,
      });
      toast.success(t('pages.crm.requestSuite.submitted', {
        defaultValue: 'Suite activation request submitted',
      }));
      setJustification('');
      setUseCase('');
      setSuiteCode('talentmap');
      setUrgency('medium');
      onOpenChange(false);
      onSubmitted?.(doc);
    } catch (e) {
      toast.error(t('pages.crm.requestSuite.failed', {
        defaultValue: 'Could not submit request',
      }), { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="request-suite-activation-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('pages.crm.requestSuite.title', { defaultValue: 'Request Suite Activation' })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.crm.requestSuite.subtitle', {
              defaultValue:
                'For the linked client. The existing suite-activation workflow will be used.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div>
            <Label>{t('pages.requests.suiteForm.suite', { defaultValue: 'Suite' })}</Label>
            <Select value={suiteCode} onValueChange={setSuiteCode}>
              <SelectTrigger data-testid="rsa-suite"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUITE_CODES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`suite.names.${s}`, { defaultValue: s })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="rsa-justification">
              {t('pages.requests.suiteForm.justification', { defaultValue: 'Justification' })}
            </Label>
            <Textarea
              id="rsa-justification"
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              data-testid="rsa-justification"
            />
          </div>

          <div>
            <Label htmlFor="rsa-usecase">
              {t('pages.requests.suiteForm.useCase', { defaultValue: 'Use case' })}
            </Label>
            <Textarea
              id="rsa-usecase"
              rows={3}
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              data-testid="rsa-usecase"
            />
          </div>

          <div>
            <Label>{t('pages.requests.suiteForm.urgency', { defaultValue: 'Urgency' })}</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger data-testid="rsa-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {URGENCIES.map((u) => (
                  <SelectItem key={u} value={u}>
                    {t(`pages.requests.priority.${u}`, { defaultValue: u })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                  data-testid="rsa-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-gradient-primary hover:opacity-90"
                  data-testid="rsa-submit">
            {saving
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t('common.saving')}</>
              : t('pages.crm.requestSuite.submit', { defaultValue: 'Submit Request' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
